export const DEFAULT_PHONE_PREFIX = '+353';

/** European + common international prefixes for CRM clients (Ireland default). */
export const PHONE_PREFIX_OPTIONS: Array<{ code: string; label: string }> = [
  { code: '+353', label: 'Ireland (+353)' },
  { code: '+355', label: 'Albania (+355)' },
  { code: '+376', label: 'Andorra (+376)' },
  { code: '+374', label: 'Armenia (+374)' },
  { code: '+43', label: 'Austria (+43)' },
  { code: '+994', label: 'Azerbaijan (+994)' },
  { code: '+375', label: 'Belarus (+375)' },
  { code: '+32', label: 'Belgium (+32)' },
  { code: '+387', label: 'Bosnia & Herz. (+387)' },
  { code: '+359', label: 'Bulgaria (+359)' },
  { code: '+385', label: 'Croatia (+385)' },
  { code: '+357', label: 'Cyprus (+357)' },
  { code: '+420', label: 'Czech Republic (+420)' },
  { code: '+45', label: 'Denmark (+45)' },
  { code: '+372', label: 'Estonia (+372)' },
  { code: '+358', label: 'Finland (+358)' },
  { code: '+33', label: 'France (+33)' },
  { code: '+995', label: 'Georgia (+995)' },
  { code: '+49', label: 'Germany (+49)' },
  { code: '+30', label: 'Greece (+30)' },
  { code: '+36', label: 'Hungary (+36)' },
  { code: '+354', label: 'Iceland (+354)' },
  { code: '+39', label: 'Italy (+39)' },
  { code: '+383', label: 'Kosovo (+383)' },
  { code: '+371', label: 'Latvia (+371)' },
  { code: '+423', label: 'Liechtenstein (+423)' },
  { code: '+370', label: 'Lithuania (+370)' },
  { code: '+352', label: 'Luxembourg (+352)' },
  { code: '+389', label: 'Macedonia (+389)' },
  { code: '+356', label: 'Malta (+356)' },
  { code: '+373', label: 'Moldova (+373)' },
  { code: '+377', label: 'Monaco (+377)' },
  { code: '+382', label: 'Montenegro (+382)' },
  { code: '+31', label: 'Netherlands (+31)' },
  { code: '+47', label: 'Norway (+47)' },
  { code: '+48', label: 'Poland (+48)' },
  { code: '+351', label: 'Portugal (+351)' },
  { code: '+7', label: 'Russia (+7)' },
  { code: '+40', label: 'Romania (+40)' },
  { code: '+378', label: 'San Marino (+378)' },
  { code: '+381', label: 'Serbia (+381)' },
  { code: '+421', label: 'Slovakia (+421)' },
  { code: '+386', label: 'Slovenia (+386)' },
  { code: '+34', label: 'Spain (+34)' },
  { code: '+46', label: 'Sweden (+46)' },
  { code: '+41', label: 'Switzerland (+41)' },
  { code: '+90', label: 'Turkey (+90)' },
  { code: '+380', label: 'Ukraine (+380)' },
  { code: '+44', label: 'United Kingdom (+44)' },
  { code: '+1', label: 'US/Canada (+1)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+91', label: 'India (+91)' },
];

export function phoneDigitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

/** Split a stored or pasted phone value into prefix and local digits. */
export function parsePhoneNumber(value: string): { prefix: string; localNumber: string } {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return { prefix: DEFAULT_PHONE_PREFIX, localNumber: '' };
  }

  let normalized = trimmed.replace(/\s+/g, ' ').trim();
  if (/^00\d/.test(normalized)) {
    normalized = `+${phoneDigitsOnly(normalized.slice(2))}`;
  }

  const plusMatch = normalized.match(/^(\+\d{1,4})\s*(.*)$/);
  if (plusMatch) {
    return {
      prefix: plusMatch[1],
      localNumber: phoneDigitsOnly(plusMatch[2]),
    };
  }

  const digits = phoneDigitsOnly(normalized);
  for (const { code } of [...PHONE_PREFIX_OPTIONS].sort((a, b) => b.code.length - a.code.length)) {
    const codeDigits = phoneDigitsOnly(code);
    if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
      return { prefix: code, localNumber: digits.slice(codeDigits.length) };
    }
  }

  return { prefix: DEFAULT_PHONE_PREFIX, localNumber: digits };
}

/** Store as "+353 896015979" (plus sign, country code, space, local number). */
export function formatPhoneNumber(prefix: string, localNumber: string): string {
  const local = phoneDigitsOnly(localNumber);
  if (!local) return '';

  const prefixDigits = phoneDigitsOnly(prefix);
  const formattedPrefix = prefix.startsWith('+') ? `+${prefixDigits}` : `+${prefixDigits}`;
  return `${formattedPrefix} ${local}`;
}

export function normalizePhoneNumber(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const { prefix, localNumber } = parsePhoneNumber(trimmed);
  return formatPhoneNumber(prefix, localNumber) || undefined;
}
